<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { useLogger } from 'vue-logger-plugin'
import { setStoreLogger } from '@/logger.ts'

import type { IAoeInvoiceResponse } from '@/types/CartTypes'

import { formatDateWithShortMonth } from "@/api/Common.ts";

const logger = useLogger()
setStoreLogger(logger)

const props = defineProps<{
  invoicecart: IAoeInvoiceResponse | null
}>()

</script>

<template>
  <div v-if="props.invoicecart != null" class="aoe-invoice-order-summary-control">
      <div class="row mt-2 section-header-row">
        <div class="col-12 section-header-div invoice-order-summary-section-header">
          {{ t('invoice.OrderInformation') }}
        </div>
      </div>
      <div class="row mt-2 order-info-row">
        <div class="col-6 col-md-2 order-info-label-div">
          {{ t('invoice.OrderReference') }}:
        </div>
        <div class="col-6 col-md-2 order-info-value-div">
          {{ props.invoicecart.item.aoeMasterOrderId }}
        </div><div class="col-6 col-md-2 order-info-label-div">
          {{ t('invoice.Name') }}:
        </div>
        <div class="col-6 col-md-2 order-info-value-div">
          {{ props.invoicecart.item.customerFirstName }} {{ props.invoicecart.item.customerLastName }}
        </div><div class="col-6 col-md-2 order-info-label-div">
          {{ t('invoice.EventName') }}:
        </div>
        <div class="col-6 col-md-2 order-info-value-div">
          {{ props.invoicecart.item.eventTitle }}
        </div>
      </div>
      <div class="row mt-2 order-info-row">
        <div class="col-6 col-md-2 order-info-label-div">
          {{ t('invoice.OrderDate') }}:
        </div>
        <div class="col-6 col-md-2 order-info-value-div">
          {{ props.invoicecart.item.invoice?.issueDate ? formatDateWithShortMonth(props.invoicecart.item.invoice.issueDate) : '' }}
        </div><div class="col-6 col-md-2 order-info-label-div">
          {{ t('invoice.Email') }}:
        </div>
        <div class="col-6 col-md-2 order-info-value-div">
          {{ props.invoicecart.item.customerEmailAddress }}
        </div><div class="col-6 col-md-2 order-info-label-div">
          {{ t('invoice.ShippingAddress') }}:
        </div>
        <div class="col-6 col-md-2 order-info-value-div">
          <div v-if="props.invoicecart.item.shippingAddress.countryCode == 'US' || props.invoicecart.item.shippingAddress.countryCode == 'CA'" class="row shipping-address-row">
            <div class="col-12 shipping-address">
              {{ props.invoicecart.item.shippingAddress.firstName }}&nbsp;{{ props.invoicecart.item.shippingAddress.lastName }}
            </div>
            <div class="col-12 shipping-address">
              {{ props.invoicecart.item.shippingAddress.addressLine1 }}
              <span v-if="props.invoicecart.item.shippingAddress.houseNumber">&nbsp;{{ props.invoicecart.item.shippingAddress.houseNumber }}</span>
            </div>
            <div class="col-12 shipping-address">
              {{ props.invoicecart.item.shippingAddress.addressLine2 }}
            </div>
            <div class="col-12 shipping-address">
              {{ props.invoicecart.item.shippingAddress.city }}<template v-if="props.invoicecart.item.shippingAddress.stateCode && props.invoicecart.item.shippingAddress.stateCode !== 'N/A'">,&nbsp;{{ props.invoicecart.item.shippingAddress.stateCode }}</template>&nbsp;{{ props.invoicecart.item.shippingAddress.postalCode }}
            </div>
          </div>
          <div v-if="props.invoicecart.item.shippingAddress.countryCode == 'AT' || props.invoicecart.item.shippingAddress.countryCode == 'DE'" class="row shipping-address-row">
            <div class="col-12 shipping-address">
              {{ props.invoicecart.item.shippingAddress.firstName }}&nbsp;{{ props.invoicecart.item.shippingAddress.lastName }}
            </div>
            <div class="col-12 shipping-address">
              {{ props.invoicecart.item.shippingAddress.addressLine1 }} {{ props.invoicecart.item.shippingAddress.houseNumber }}
            </div>
            <div class="col-12 shipping-address">
              {{ props.invoicecart.item.shippingAddress.addressLine2 }}
            </div>
            <div class="col-12 shipping-address">
              {{ props.invoicecart.item.shippingAddress.postalCode }}&nbsp;{{ props.invoicecart.item.shippingAddress.city }},&nbsp;{{ props.invoicecart.item.shippingAddress.countryCode }}
            </div>
          </div>
          <div v-if="props.invoicecart.item.shippingAddress.countryCode == 'FR'" class="row shipping-address-row">
            <div class="col-12 shipping-address">
              {{ props.invoicecart.item.shippingAddress.firstName }}&nbsp;{{ props.invoicecart.item.shippingAddress.lastName }}
            </div>
            <div class="col-12 shipping-address">
              {{ props.invoicecart.item.shippingAddress.houseNumber }} {{ props.invoicecart.item.shippingAddress.addressLine1 }}
            </div>
            <div class="col-12 shipping-address">
              {{ props.invoicecart.item.shippingAddress.addressLine2 }}
            </div>
            <div class="col-12 shipping-address">
              {{ props.invoicecart.item.shippingAddress.postalCode }}&nbsp;{{ props.invoicecart.item.shippingAddress.city }},&nbsp;{{ props.invoicecart.item.shippingAddress.countryCode }}
            </div>
          </div>
        </div>
      </div>
      <div class="row mt-2 order-info-row">
        <div class="col-6 col-md-2 order-info-label-div">
          {{ t('invoice.CartNumber') }}:
        </div>
        <div class="col-6 col-md-2 order-info-value-div">
          {{ props.invoicecart.item.aoeCartId }}
        </div><div class="col-6 col-md-2 order-info-label-div">
          {{ t('invoice.Phone') }}:
        </div>
        <div class="col-6 col-md-2 order-info-value-div">
          {{ props.invoicecart.item.customerPhoneNumber }}
        </div>
      </div>
      <div class="row mt-2 order-info-row">
        <div class="col-6 col-md-2 order-info-label-div">
          {{ t('invoice.InvoiceNumber') }}:
        </div>
        <div class="col-6 col-md-2 order-info-value-div">
          {{ props.invoicecart.item.invoice?.invoiceNumber }}
        </div>
      </div>
  </div>
</template>

<style lang="scss">

.shipping-address-row
{
  position: absolute;
}

</style>
